/**
 * Notification Service
 *
 * Handles API calls for user notifications.
 */

import api from './api';

export type NotificationType =
  | 'follow'
  | 'comment'
  | 'favorite'
  | 'rating'
  | 'stream_upload'
  | 'mention';

export interface Notification {
  userId: string;
  notificationId: string;
  type: NotificationType;
  actorId: string;
  actorUsername: string;
  actorDisplayName?: string;
  actorProfilePicture?: string;
  targetId?: string;
  targetType?: string;
  targetName?: string;
  message: string;
  isRead: number;
  createdAt: number;
  timeAgo: string;
}

export interface NotificationsResponse {
  notifications: Notification[];
  nextToken?: string;
}

export interface UnreadCountResponse {
  count: number;
}

export const notificationService = {
  /**
   * Get notifications for the current user
   */
  async getNotifications(limit: number = 20, cursor?: string): Promise<NotificationsResponse> {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (cursor) {
      params.append('cursor', cursor);
    }
    return api.get<NotificationsResponse>(`/notifications?${params.toString()}`);
  },

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response = await api.get<UnreadCountResponse>('/notifications/unread-count');
    return response.count;
  },

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    await api.put(`/notifications/${notificationId}/read`);
  },

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<{ count: number }> {
    return api.post<{ count: number }>('/notifications/mark-all-read');
  },

  /**
   * Delete a notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    await api.delete(`/notifications/${notificationId}`);
  }
};

export default notificationService;
